import { Box, Typography, Paper } from '@mui/material';
import { Grid2, type Grid2Props } from '@mui/material';
import { Formik, Form } from 'formik';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import type { Schema } from 'yup';
import { object, string } from 'yup';

import { ConfirmationDialog } from '@components/ConfirmationDialog';
import ContentLoader from '@components/ContentLoader';
import RoundedButton from '@components/RoundedButton';

import { useUpdateAccountSettings } from '@hooks/api/account-settings';

import type { GetAccountSettingQuery } from 'generated/license.generated';

const messages = defineMessages({
  primaryColor: {
    id: 'organization.setting.primaryColor',
    defaultMessage: 'Primary',
  },
  secondaryColor: {
    id: 'organization.setting.secondaryColor',
    defaultMessage: 'Secondary',
  },
  save: {
    id: 'organization.setting.save',
    defaultMessage: 'Save Colors',
  },
  noColor: {
    id: 'organization.setting.noColor',
    defaultMessage: 'No color selected!',
  },
  delete: {
    id: 'organization.setting.delete',
    defaultMessage: 'Delete Colors',
  },
  success: {
    id: 'changeColors.success',
    defaultMessage: 'Successfully updated primary and secondary colors',
  },
  deleteConfirm: {
    id: 'organization.setting.changeColor.deleteConfirm',
    defaultMessage:
      'Are you sure you want to delete your primary and secondary colors?',
  },
  required: {
    id: 'GLOBAL.required',
    defaultMessage: 'This field is required',
  },
});

interface BrandColorsFormProps {
  accountSettings?: GetAccountSettingQuery;
}
type FormValues = {
  primaryColor: string | null;
  secondaryColor: string | null;
};

export default function BrandColorsForm({
  accountSettings,
}: BrandColorsFormProps) {
  const { formatMessage } = useIntl();

  const validationSchema: Schema<FormValues> = object({
    primaryColor: string().required(formatMessage(messages.required)),
    secondaryColor: string().required(formatMessage(messages.required)),
  });
  const [updateAccountSetting, saving] = useUpdateAccountSettings();

  const initialValues = {
    primaryColor:
      accountSettings?.getAccountSetting?.branding_color_primary || '',
    secondaryColor:
      accountSettings?.getAccountSetting?.branding_color_secondary || '',
  };

  const handleDelete = async () => {
    await updateAccountSetting({
      account_id: accountSettings?.getAccountSetting?.account_id ?? '',
      branding_color_primary: null,
      branding_color_secondary: null,
    });
  };
  const handleSubmit = async (values: FormValues) => {
    await updateAccountSetting({
      account_id: accountSettings?.getAccountSetting?.account_id ?? '',
      branding_color_primary: values.primaryColor,
      branding_color_secondary: values.secondaryColor,
    });
  };
  const gridItemProps: Grid2Props = {
    size: {
      xs: 12,
      md: 6,
    },
  };
  return (
    <ContentLoader lineCount={3} loading={false}>
      <Formik
        initialValues={initialValues}
        enableReinitialize
        onSubmit={handleSubmit}
        validationSchema={validationSchema}
      >
        {({ dirty, values, handleChange, isSubmitting }) => (
          <Form>
            <Grid2 container spacing={2}>
              {(['primaryColor', 'secondaryColor'] as const).map((key) => (
                <Grid2 {...gridItemProps} key={key}>
                  <Paper
                    elevation={1}
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      gap: 1.5,
                      transition: '0.3s',
                      '&:hover': { boxShadow: 6 },
                    }}
                  >
                    <Typography color="primary" variant="subtitle2">
                      {formatMessage(messages[key as keyof typeof messages])}
                    </Typography>

                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        width: '100%',
                      }}
                    >
                      <Box
                        component="input"
                        type="color"
                        name={key}
                        value={values[key] || '#ffffff'}
                        onChange={handleChange}
                        sx={{
                          width: 60,
                          height: 60,
                          borderRadius: 2,
                          border: 'ActiveBorder',
                          cursor: 'pointer',
                          flexShrink: 0,
                        }}
                      />
                      <Typography variant="body2" color="text.secondary">
                        {!values[key]
                          ? formatMessage(messages.noColor)
                          : values[key]}
                      </Typography>
                    </Box>
                  </Paper>
                </Grid2>
              ))}
              <Grid2 size={{ sm: 6, xs: 6 }}>
                <RoundedButton
                  type="submit"
                  disabled={
                    !dirty ||
                    !values.primaryColor ||
                    !values.secondaryColor ||
                    isSubmitting ||
                    saving
                  }
                >
                  {formatMessage(messages.save)}
                </RoundedButton>
              </Grid2>

              <Grid2 size={{ sm: 6, xs: 6 }}>
                <ConfirmationDialog
                  onConfirm={handleDelete}
                  title={formatMessage(messages.deleteConfirm)}
                >
                  {({ handleOpen }) => (
                    <RoundedButton
                      disabled={
                        initialValues.primaryColor &&
                        initialValues.secondaryColor
                          ? false
                          : true
                      }
                      onClick={handleOpen}
                      variant="red"
                    >
                      {formatMessage(messages.delete)}
                    </RoundedButton>
                  )}
                </ConfirmationDialog>
              </Grid2>
            </Grid2>
          </Form>
        )}
      </Formik>
    </ContentLoader>
  );
}
